import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowDown,
  ArrowRight,
  ArrowUpRight,
  Brain,
  Check,
  Crown,
  Dna,
  Download,
  Dumbbell,
  FileText,
  FlaskConical,
  HandHeart,
  HeartPulse,
  Lock,
  Microscope,
  Salad,
  Radar,
  ScanFace,
  Sparkles,
  Stethoscope,
  Sun,
  Watch,
  X,
} from "lucide-react";
import { LabsLogo } from "@/components/Logo";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Level 1 entry card: soft-locked until Level 0 is complete (independent access allowed) */
const Level1EntryCard = () => {
  let unlocked = false;
  try {
    unlocked = !!JSON.parse(sessionStorage.getItem("tlp-l0-result"))?.token;
  } catch { /* noop */ }
  return (
    <motion.div
      {...fade(0.1)}
      className="mx-auto mt-12 max-w-xl"
    >
      <Link
        to="/assessment/level-1?src=backus_card"
        data-testid="level1-entry-card"
        title={unlocked ? "Level 1 Assessment" : "Complete Level 0 to unlock"}
        className="group relative flex items-center gap-4 overflow-hidden rounded-2xl border border-violet-300/40 bg-gradient-to-r from-violet-400/15 via-surface to-cyan-400/10 p-5 text-left shadow-[0_0_44px_-10px_rgba(167,139,250,0.5)] transition-all duration-300 hover:border-violet-300/70 hover:shadow-[0_0_56px_-8px_rgba(167,139,250,0.65)]"
      >
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-violet-400/20">
          {unlocked ? <ScanFace className="h-6 w-6 text-violet-300" /> : <Lock className="h-5 w-5 text-violet-300" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-white">Mind &amp; Psychology</p>
          <p className="mt-0.5 text-sm font-light text-white/80">Level 1 Assessment: Assess your Current Mental Marker.</p>
          {!unlocked && <p className="mt-1 text-xs font-medium text-violet-300/90">Complete Level 0 to unlock</p>}
        </div>
        <ArrowUpRight className="h-5 w-5 shrink-0 text-violet-300 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </Link>

      {/* Level 2 entry card: RiskRadar Markers, open to everyone */}
      <Link
        to="/assessment/level-2?src=backus_card"
        data-testid="level2-entry-card"
        title="Level 2 Assessment"
        className="group relative mt-4 flex items-center gap-4 overflow-hidden rounded-2xl border border-cyan-300/40 bg-gradient-to-r from-cyan-400/15 via-surface to-green-400/10 p-5 text-left shadow-[0_0_44px_-10px_rgba(34,211,238,0.45)] transition-all duration-300 hover:border-cyan-300/70 hover:shadow-[0_0_56px_-8px_rgba(34,211,238,0.6)]"
      >
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-cyan-400/20">
          <Radar className="h-6 w-6 text-cyan-300" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-white">
            <span className="mr-1.5 rounded-full bg-amber-400/15 px-1.5 py-0.5 align-middle text-[9px] font-bold uppercase tracking-wider text-amber-300">Beta</span>
            RiskRadar Markers
          </p>
          <p className="mt-0.5 text-sm font-light text-white/80">Level 2 Assessment: your city's external risk profile, tuned for your age and sex.</p>
        </div>
        <ArrowUpRight className="h-5 w-5 shrink-0 text-cyan-300 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </Link>
    </motion.div>
  );
};

const inputCls =
  "w-full rounded-xl border border-white/10 bg-surface px-5 py-4 text-base text-white placeholder-white/30 outline-none transition-colors duration-300 focus:border-cyan-300/50";
const labelCls = "mb-2 block text-left text-base font-medium text-white/70";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
});

const SectionHead = ({ eyebrow, title, sub, testId }) => (
  <motion.div {...fade()} data-testid={testId} className="text-center">
    <p className="text-gradient mb-6 font-mono text-base uppercase tracking-[0.3em]">{eyebrow}</p>
    <h2 className="mx-auto max-w-3xl font-display text-[2rem] font-semibold leading-[1.08] tracking-tight sm:text-[2.75rem]">
      {title}
    </h2>
    {sub && (
      <p className="mx-auto mt-6 max-w-2xl text-lg font-light leading-relaxed text-fog">{sub}</p>
    )}
  </motion.div>
);

const REGION = {
  intl: { label: "Outside India", sym: "$", currency: "usd" },
  in: { label: "India", sym: "₹", currency: "inr" },
};

const plans = [
  {
    id: "prime",
    name: "Prime Founding Member",
    icon: Sparkles,
    prices: { intl: "$20", in: "₹1,900" },
    usdHint: "≈ $20",
    inrNote: "billed in INR (₹1,900)",
    cadence: "per month",
    cadenceK: "cad.month",
    mode: "monthly",
    amounts: { usd: 20, inr: 1900 },
    tagK: "plan.prime.tag",
    tagline: "The entry point into the Lifespan ecosystem.",
    fKeys: ["plan.prime.f1", "plan.prime.f2", "plan.prime.f3", "plan.prime.f4", "plan.prime.f5"],
    features: [
      "Digital Founding Membership card with scannable barcode",
      "All published research, content and videos",
      "Access to The Lifespan Labs when launched",
      "Member offers and preferred pricing",
      "Submit research for peer-review consideration",
    ],
  },
  {
    id: "pro",
    name: "Pro Founding Member",
    icon: Crown,
    prices: { intl: "$2,000", in: "₹1,90,000" },
    usdHint: "≈ $2,000",
    inrNote: "billed in INR (₹1,90,000)",
    cadence: "lifetime",
    cadenceK: "cad.life",
    mode: "one_time",
    amounts: { usd: 2000, inr: 190000 },
    tagK: "plan.pro.tag",
    tagline: "Everything in Prime, for life. Plus the inner circle.",
    fKeys: ["plan.pro.f1", "plan.pro.f2", "plan.pro.f3", "plan.pro.f4", "plan.pro.f5"],
    features: [
      "Lifetime membership, no renewals",
      "Pro-only research releases and exclusives",
      "Invitations to research meetings and events worldwide",
      "Enhanced discounts across The Lifespan Labs",
      "Priority access to new products and services",
    ],
  },
  {
    id: "wellwisher",
    name: "Wellwisher Founding Member",
    icon: HandHeart,
    prices: { intl: "Free", in: "Free" },
    cadence: "forever",
    cadenceK: "cad.free",
    tagK: "plan.well.tag",
    tagline: "Stay close to the science, at no cost.",
    fKeys: ["plan.well.f1", "plan.well.f2", "plan.well.f3"],
    features: [
      "Digital Wellwisher Membership card with barcode",
      "Research news and updates",
      "First to know when The Lifespan Labs launches",
    ],
  },
];

const RegionSwitch = ({ region, onChange }) => {
  const { t } = useLang();
  return (
  <div data-testid="region-switch" className="mx-auto flex w-fit rounded-full border border-white/10 bg-surface p-1.5">
    {Object.entries(REGION).map(([id, r]) => (
      <button
        key={id}
        type="button"
        data-testid={`region-${id}`}
        onClick={() => onChange(id)}
        className={`rounded-full px-5 py-2.5 text-base font-medium transition-colors duration-300 sm:px-6 ${
          region === id ? "bg-gradient-accent text-white" : "text-fog hover:text-white"
        }`}
      >
        {t(`reg.${id}`, r.label)} · {r.sym}
      </button>
    ))}
  </div>
  );
};

const categories = [
  { icon: HeartPulse, nk: "eco.c1n", name: "Health", desc: "Health-oriented products, services and technologies." },
  { icon: Salad, nk: "eco.c2n", name: "Food & Nutrition", desc: "Nutrition, food, supplements and longevity-oriented dietary solutions." },
  { icon: Microscope, nk: "eco.c3n", name: "Biomarkers & Labs", desc: "Biomarker testing, diagnostics, laboratory services and measurement technologies." },
  { icon: Stethoscope, nk: "eco.c4n", name: "Doctors & Health Services", desc: "Access to participating healthcare professionals and relevant services." },
  { icon: Watch, nk: "eco.c5n", name: "Devices & Machines", desc: "Emerging technologies, devices and machines designed around health, longevity and human performance." },
  { icon: Dumbbell, nk: "eco.c6n", name: "Sports & Body", desc: "Fitness, physical performance, recovery and bodily health." },
  { icon: HandHeart, nk: "eco.c7n", name: "Healing & Physiotherapy", desc: "Physiotherapy, rehabilitation, recovery and relevant therapeutic services." },
  { icon: Brain, nk: "eco.c8n", name: "Mind & Psychology", desc: "Psychological tools, cognitive services, mental wellbeing and human-performance programs." },
  { icon: Sun, nk: "eco.c9n", name: "Mind · Body · Soul", desc: "Programs and services supporting integrated human wellbeing." },
];
const flow = ["Research", "Validation", "Technology", "Products & Services", "Longer, Healthier Life"];
const flowKeys = ["flow.1", "flow.2", "flow.3", "flow.4", "flow.5"];

const whyCards = [
  { icon: FlaskConical, tk: "why.1t", title: "Support the Research", desc: "Help build the scientific and technological foundation of The Lifespan Project." },
  { icon: Sparkles, tk: "why.2t", title: "Access the Ecosystem", desc: "Gain Digital Membership to the emerging Lifespan Labs marketplace." },
  { icon: Dna, tk: "why.3t", title: "Discover What Comes Next", desc: "Receive access to selected future products, technologies, services and research developments." },
  { icon: Crown, tk: "why.4t", title: "Help Shape the Future", desc: "Become part of the founding community surrounding one of humanity's oldest scientific questions: how far can human life be extended?" },
];

const NetworkVisual = () => {
  const { t } = useLang();
  return (
  <div data-testid="global-network-visual" className="relative mx-auto mt-14 h-72 w-full max-w-xl">
    <svg viewBox="0 0 400 260" className="h-full w-full animate-[spin_90s_linear_infinite]" fill="none">
      <circle cx="200" cy="130" r="120" stroke="rgba(255,255,255,0.08)" />
      <circle cx="200" cy="130" r="80" stroke="rgba(255,255,255,0.06)" />
      <circle cx="200" cy="130" r="40" stroke="rgba(34,211,238,0.25)" />
      {[
        [200, 10], [320, 130], [200, 250], [80, 130],
        [285, 45], [285, 215], [115, 45], [115, 215],
      ].map(([x, y], i) => (
        <g key={i}>
          <line x1="200" y1="130" x2={x} y2={y} stroke="rgba(139,92,246,0.18)" />
          <circle cx={x} cy={y} r="4" fill="#22d3ee" opacity="0.8" />
        </g>
      ))}
      <circle cx="200" cy="130" r="6" fill="#a78bfa" />
    </svg>
    <p className="mt-2 text-center font-mono text-base uppercase tracking-[0.3em] text-white/40">
      {t("net.caption", "Global research network")}
    </p>
  </div>
  );
};

export default function BackUsPage() {
  const { t } = useLang();
  const [plan, setPlan] = useState("prime");
  const [region, setRegion] = useState("intl");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [status, setStatus] = useState("idle");
  const [payError, setPayError] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ code: "", name: "", email: "" });
  const [inviteStatus, setInviteStatus] = useState("idle");
  const [inviteError, setInviteError] = useState("");
  const [wwOpen, setWwOpen] = useState(false);
  const [wwForm, setWwForm] = useState({ name: "", email: "", phone: "", country: "", city: "" });
  const [wwStatus, setWwStatus] = useState("idle");
  const [wwMemberId, setWwMemberId] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setInv = (k) => (e) => setInvite((f) => ({ ...f, [k]: e.target.value }));
  const setWw = (k) => (e) => setWwForm((f) => ({ ...f, [k]: e.target.value }));

  /* Razorpay checkout.js (loaded once, used when the member pays) */
  useEffect(() => {
    if (document.getElementById("rzp-checkout-js")) return;
    const s = document.createElement("script");
    s.id = "rzp-checkout-js";
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  const active = plans.find((p) => p.id === plan);
  const paidPlans = plans.filter((p) => p.id !== "wellwisher");

  const choose = (id) => setPlan(id);

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setPayError("");
    setStatus("sending");
    try {
      await fetch(`${API}/leads/backers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, amount: active.amounts.inr, frequency: active.mode, tier: plan, currency: "inr" }),
      });
      const res = await fetch(`${API}/payments/rzp/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan: active.id, name: form.name, email: form.email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not start the payment.");
      if (!window.Razorpay) throw new Error("The payment window failed to load. Check your connection and retry.");
      const ref = data.kind === "subscription" ? data.subscription_id : data.order_id;
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "The Lifespan Project",
        description: active.id === "prime" ? "Prime Founding Membership (monthly)" : "Pro Founding Membership (lifetime)",
        ...(data.kind === "subscription" ? { subscription_id: data.subscription_id } : { order_id: data.order_id }),
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: "#22d3ee", backdrop_color: "rgba(5,7,14,0.85)" },
        modal: { ondismiss: () => setStatus("idle") },
        handler: async (resp) => {
          try {
            const v = await fetch(`${API}/payments/rzp/verify`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                plan: active.id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_subscription_id: resp.razorpay_subscription_id,
              }),
            });
            if (!v.ok) throw new Error("verify failed");
            window.location.href = `/payment/success?ref=${encodeURIComponent(ref)}`;
          } catch {
            window.location.href = "/payment/cancel";
          }
        },
      });
      rzp.on("payment.failed", () => { window.location.href = "/payment/cancel"; });
      rzp.open();
      setStatus("idle");
    } catch (err) {
      setPayError(err && err.message ? String(err.message) : "");
      setStatus("error");
    }
  };

  const redeemInvite = async (e) => {
    e.preventDefault();
    if (inviteStatus === "sending") return;
    setInviteError("");
    setInviteStatus("sending");
    try {
      const res = await fetch(`${API}/payments/invite/redeem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan, code: invite.code, name: invite.name, email: invite.email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not redeem that code.");
      window.location.href = `/payment/success?ref=${encodeURIComponent(data.ref)}`;
    } catch (err) {
      setInviteError(String(err.message || "Could not redeem that code."));
      setInviteStatus("error");
    }
  };

  const submitWellwisher = async (e) => {
    e.preventDefault();
    setWwStatus("sending");
    try {
      const res = await fetch(`${API}/leads/subscribers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...wwForm, tier: "wellwisher" }),
      });
      if (!res.ok) throw new Error("failed");
      const data = await res.json();
      setWwMemberId(data.membership_id || null);
      setWwStatus("done");
    } catch {
      setWwStatus("error");
    }
  };

  const closeWellwisher = () => {
    setWwOpen(false);
    setWwStatus("idle");
    setWwMemberId(null);
    setWwForm({ name: "", email: "", phone: "", country: "", city: "" });
  };

  return (
    <main className="relative overflow-hidden">
      <div className="pointer-events-none absolute -top-48 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(34,211,238,0.12),transparent)]" />

      {/* 2. HERO */}
      <section data-testid="membership-hero" className="relative z-10 mx-auto w-full max-w-5xl px-6 pb-24 pt-40 text-center md:pt-48">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="badge-animated inline-flex items-center gap-3 rounded-full px-6 py-3 backdrop-blur">
            <span className="text-base font-medium text-white/85">{t("bu.badge", "Advancing the science of human longevity")}</span>
          </span>
          <h1 className="mx-auto mt-10 max-w-4xl font-display text-[2rem] font-bold leading-[1.08] tracking-tight sm:text-[2.75rem] lg:text-[3.5rem]">
            {t("bu.h1a", "Become a")} <span className="text-gradient">{t("bu.h1b", "Founding Member")}</span> {t("bu.h1c", "of The Lifespan Project.")}
          </h1>
          <p className="mx-auto mt-8 max-w-2xl text-lg font-light leading-relaxed text-fog">
            {t("bu.sub", "The future of human longevity will not be built by one discipline, one laboratory or one technology. It will be built through research, experimentation, collaboration and the translation of scientific knowledge into tangible solutions. Your membership helps support that journey.")}
          </p>
          <div className="mx-auto mt-10 flex max-w-2xl flex-col items-center justify-center gap-3 sm:flex-row sm:gap-8">
            <p className="text-base text-white/80">
              <span className="font-semibold text-white">The Lifespan Project</span>{t("bu.projline", ": researching the science.")}
            </p>
            <ArrowRight className="hidden h-4 w-4 text-cyan-300 sm:block" />
            <p className="text-base text-white/80">
              <span className="font-semibold text-white">The Lifespan Labs</span>{t("bu.labsline", ": bringing longevity solutions to life.")}
            </p>
          </div>
        </motion.div>
      </section>

      {/* 1. BRAND ARCHITECTURE */}
      <section data-testid="brand-architecture" className="relative z-10 border-t border-white/5 py-24 md:py-32">
        <div className="mx-auto max-w-6xl px-6">
          <SectionHead
            testId="brand-architecture-head"
            eyebrow={t("arch.eyebrow", "The Structure")}
            title={<>{t("arch.h2a", "One mission.")} <span className="text-gradient">{t("arch.h2b", "Two engines.")}</span></>}
          />
          <div className="mt-16 grid gap-6 md:grid-cols-2">
            <motion.div {...fade(0.05)} data-testid="entity-project" className="rounded-3xl border border-white/10 bg-surface p-10 text-left">
              <p className="font-mono text-base uppercase tracking-[0.25em] text-cyan-300">The Lifespan Project</p>
              <h3 className="mt-4 font-display text-2xl font-semibold tracking-tight">{t("arch.proj.h", "Scientific R&D")}</h3>
              <p className="mt-4 text-base font-light leading-relaxed text-fog">
                The research organization working across fundamental science, physics,
                biology, neuroscience, AI, psychology, nutrition, human performance and
                other relevant disciplines to investigate the mechanisms and
                possibilities underlying substantially longer human life. Its long-term
                scientific objective is to develop theoretical foundations, models,
                technologies and tangible solutions that can push the boundaries of
                human longevity.
              </p>
            </motion.div>
            <motion.div {...fade(0.15)} data-testid="entity-labs" className="rounded-3xl border border-white/10 bg-surface p-10 text-left">
              <p className="flex items-center gap-3 font-mono text-base uppercase tracking-[0.25em] text-violet-300">
                <LabsLogo className="h-7 w-7" />
                The Lifespan Labs
              </p>
              <h3 className="mt-4 font-display text-2xl font-semibold tracking-tight">{t("arch.labs.h", "Longevity Marketplace")}</h3>
              <p className="mt-4 text-base font-light leading-relaxed text-fog">
                The upcoming marketplace and ecosystem bringing together products,
                technologies, services and solutions relevant to longer, healthier and
                better human life. The Labs will include solutions developed by The
                Lifespan Project as well as carefully selected and certified external
                products and services.
              </p>
            </motion.div>
          </div>
          <motion.div
            {...fade(0.2)}
            data-testid="research-flow"
            className="mt-12 flex flex-wrap items-center justify-center gap-3 rounded-3xl border border-white/10 bg-surface/60 px-6 py-8"
          >
            {flow.map((step, i) => (
              <span key={step} className="flex items-center gap-3">
                <span className={`font-mono text-base uppercase tracking-[0.2em] ${i === flow.length - 1 ? "text-gradient-green font-semibold" : "text-white/70"}`}>
                  {t(flowKeys[i], step)}
                </span>
                {i < flow.length - 1 && <ArrowRight className="h-4 w-4 text-cyan-300/60" />}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. POSITIONING */}
      <section data-testid="positioning-section" className="relative z-10 border-t border-white/5 bg-surface/50 py-24 md:py-32">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <motion.div {...fade()}>
            <p className="text-gradient mb-6 font-mono text-base uppercase tracking-[0.3em]">{t("pos.eyebrow", "What Membership Means")}</p>
            <h2 className="font-display text-4xl font-semibold leading-[1.08] tracking-tight sm:text-5xl">
              {t("pos.h2", "You are not simply subscribing to content.")}
            </h2>
            <p className="mx-auto mt-8 max-w-2xl text-lg font-light leading-relaxed text-fog">
              {t("pos.p", "You are becoming part of the founding community behind the research. Membership supports the continued development of research, scientific work, knowledge, experimentation and the future solutions pipeline of The Lifespan Project. In return, Founding Members receive a Digital Founding Membership that connects them to the growing Lifespan ecosystem.")}
            </p>
            <p className="mx-auto mt-6 max-w-2xl text-base font-light leading-relaxed text-white/50">
              {t("pos.note", "Membership does not guarantee any scientific result, health outcome or increase in lifespan.")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* 5. ECOSYSTEM CATEGORIES */}
      <section data-testid="ecosystem-section" className="relative z-10 border-t border-white/5 py-24 md:py-32">
        <div className="mx-auto max-w-6xl px-6">
          <SectionHead
            testId="ecosystem-head"
            eyebrow={t("eco.eyebrow", "Digital Founding Membership")}
            title={<>{t("eco.h2a", "Your membership extends")} <span className="text-gradient">{t("eco.h2b", "beyond the research.")}</span></>}
            sub={t("eco.sub", "Every Founding Member receives a Digital Membership to the growing Lifespan ecosystem, including access to the upcoming The Lifespan Labs marketplace.")}
          />
          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {categories.map((c, i) => (
              <motion.div
                key={c.name}
                {...fade((i % 3) * 0.06)}
                data-testid={`category-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="rounded-2xl border border-white/10 bg-surface p-8 text-left transition-colors duration-300 hover:border-cyan-300/30"
              >
                <c.icon className="h-6 w-6 text-cyan-300" />
                <h3 className="mt-5 font-display text-xl font-semibold tracking-tight">{t(c.nk, c.name)}</h3>
                <p className="mt-3 text-base font-light leading-relaxed text-fog">{c.desc}</p>
              </motion.div>
            ))}
          </div>
          <motion.p {...fade()} className="mx-auto mt-12 max-w-3xl text-center text-base font-light leading-relaxed text-white/50">
            Only products and services that meet The Lifespan Labs' internal standards
            for relevance, quality and longevity-oriented value will be eligible for
            certification within the ecosystem.
          </motion.p>
          <Level1EntryCard />
        </div>
      </section>

      {/* 6 & 7. BENEFITS */}
      <section id="plans" data-testid="benefits-section" className="relative z-10 border-t border-white/5 bg-surface/50 py-24 md:py-32">
        <div className="mx-auto max-w-6xl px-6">
          <SectionHead testId="benefits-head" eyebrow={t("ben.eyebrow", "Founding Member Benefits")} title={t("ben.h", "What Founding Members receive")} />
          <div className="mt-10">
            <RegionSwitch region={region} onChange={setRegion} />
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {plans.map((p, i) => (
              <motion.div
                key={p.id}
                {...fade(i * 0.1)}
                data-testid={`plan-card-${p.id}`}
                className={`flex h-full flex-col rounded-3xl border bg-surface p-8 text-left ${
                  p.id === "pro" ? "border-violet-400/30" : "border-white/10"
                }`}
              >
                <div className="flex items-center justify-between">
                  <p.icon className={`h-5 w-5 ${p.id === "pro" ? "text-violet-300" : "text-cyan-300"}`} />
                  <span className="font-mono text-sm uppercase tracking-[0.2em] text-white/50">
                    {p.id === "wellwisher" ? t("mode.free", "Free") : p.mode === "monthly" ? t("mode.monthly", "Monthly") : t("mode.life", "Lifetime")}
                  </span>
                </div>
                <h3 className="mt-4 font-display text-xl font-semibold tracking-tight">{p.name}</h3>
                <p className="mt-3">
                  <span className="font-display text-4xl font-bold tracking-tight">{p.prices[region]}</span>
                  <span className="ml-2 text-base font-light text-fog">{t(p.cadenceK, p.cadence)}</span>
                  {p.id !== "wellwisher" && (
                    <span className="ml-2 text-xs font-light text-white/35">
                      {region === "in" ? p.usdHint : p.inrNote}
                    </span>
                  )}
                </p>
                <p className="mt-2 text-sm font-light text-fog">{t(p.tagK, p.tagline)}</p>
                <ul className="mt-7 grid flex-1 content-start gap-2.5">
                  {p.features.map((f, fi) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm font-light text-fog">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />
                      {t(p.fKeys[fi], f)}
                    </li>
                  ))}
                </ul>
                {p.id === "wellwisher" ? (
                  <button
                    type="button"
                    onClick={() => setWwOpen(true)}
                    data-testid="cta-wellwisher"
                    className="group mt-8 flex items-center justify-center gap-2.5 rounded-full bg-white px-6 py-3 text-base font-semibold text-black shadow-[0_8px_32px_rgba(226,232,240,0.25)] transition-shadow duration-300 hover:shadow-[0_8px_44px_rgba(226,232,240,0.4)]"
                  >
                    {t("cta.free", "Subscribe Free")}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                ) : (
                  <a
                    href="#membership-checkout"
                    onClick={() => choose(p.id)}
                    data-testid={`cta-${p.id}`}
                    className={`group mt-8 flex w-fit items-center justify-center gap-2.5 self-center rounded-full px-8 py-3 text-base font-semibold transition-shadow duration-300 ${
                      p.id === "pro"
                        ? "bg-gradient-accent text-white shadow-[0_8px_32px_rgba(59,130,246,0.35)] hover:shadow-[0_8px_44px_rgba(34,211,238,0.5)]"
                        : "bg-gradient-green text-black shadow-[0_8px_32px_rgba(74,222,128,0.30)] hover:shadow-[0_8px_44px_rgba(163,230,53,0.45)]"
                    }`}
                  >
                    {t("cta.subscribe", "Subscribe Here")}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section data-testid="why-section" className="relative z-10 border-t border-white/5 py-24 md:py-32">
        <div className="mx-auto max-w-6xl px-6">
          <SectionHead testId="why-head" eyebrow={t("why.eyebrow", "The Case")} title={t("why.h", "Why become a Founding Member?")} />
          <div className="mt-16 grid gap-6 sm:grid-cols-2">
            {whyCards.map((c, i) => (
              <motion.div
                key={c.title}
                {...fade((i % 2) * 0.08)}
                data-testid={`why-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="rounded-3xl border border-white/10 bg-surface p-10 text-left transition-colors duration-300 hover:border-cyan-300/30"
              >
                <c.icon className="h-7 w-7 text-cyan-300" />
                <h3 className="mt-6 font-display text-2xl font-semibold tracking-tight">{t(c.tk, c.title)}</h3>
                <p className="mt-4 text-base font-light leading-relaxed text-fog">{c.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT CONNECTS */}
      <section data-testid="brand-flow" className="relative z-10 border-t border-white/5 py-24 md:py-32">
        <div className="mx-auto max-w-4xl px-6">
          <SectionHead testId="brand-flow-head" eyebrow={t("flowS.eyebrow", "How It Connects")} title={t("flowS.h", "From research to community")} />
          <motion.div {...fade()} className="mx-auto mt-16 flex max-w-2xl flex-col items-center gap-0">
            {[
              { name: "The Lifespan Project", tags: "R&D · Science · Research · Technology", tagK: "flowS.n1t", accent: "text-cyan-300" },
              { name: "The Lifespan Labs", tags: "Products · Services · Marketplace · Access", tagK: "flowS.n2t", accent: "text-violet-300" },
              { name: "Founding Members", tags: "Community · Access · Participation", tagK: "flowS.n3t", accent: "text-gradient-green" },
            ].map((n, i) => (
              <div key={n.name} className="flex w-full flex-col items-center">
                <div
                  data-testid={`flow-node-${i + 1}`}
                  className="w-full rounded-2xl border border-white/10 bg-surface px-8 py-8 text-center"
                >
                  <p className="flex items-center justify-center gap-3 font-display text-2xl font-semibold tracking-tight">
                    {i === 1 && <LabsLogo className="h-7 w-7" />}
                    {n.name}
                  </p>
                  <p className={`mt-2 font-mono text-base uppercase tracking-[0.2em] ${n.accent}`}>{t(n.tagK, n.tags)}</p>
                </div>
                {i < 2 && <ArrowDown className="my-3 h-5 w-5 text-cyan-300/60" />}
              </div>
            ))}
          </motion.div>
          <NetworkVisual />
          <motion.p {...fade()} className="mx-auto mt-6 max-w-2xl text-center text-base font-light leading-relaxed text-fog">
            {t("flowS.proline", "Pro Founding Members may receive invitations to selected Lifespan Project research meetings, conferences and ecosystem events across the world.")}
          </motion.p>
        </div>
      </section>

      {/* CHECKOUT */}
      <section id="membership-checkout" data-testid="membership-checkout" className="relative z-10 border-t border-white/5 py-24 md:py-32">
        <div className="mx-auto max-w-3xl px-6">
          <SectionHead testId="checkout-head" eyebrow={t("ck.eyebrow", "Become a Founding Member")} title={t("ck.h", "Activate your membership")} />
          <motion.div {...fade()} className="mt-14 rounded-3xl bg-gradient-to-br from-violet-500/40 via-blue-500/15 to-cyan-400/40 p-[1px]">
            <div className="rounded-3xl bg-surface p-8 md:p-12">
              <form data-testid="backer-form" onSubmit={submit} className="grid gap-8">
                <div className="grid gap-4">
                  <RegionSwitch region={region} onChange={setRegion} />
                  <div className="mx-auto flex w-fit rounded-full border border-white/10 bg-surface p-1.5">
                    {paidPlans.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        data-testid={`backer-plan-${p.id}`}
                        onClick={() => choose(p.id)}
                        className={`rounded-full px-6 py-3 text-base font-medium transition-colors duration-300 sm:px-8 ${
                          plan === p.id ? "bg-gradient-accent text-white" : "text-fog hover:text-white"
                        }`}
                      >
                        {p.id === "pro"
                          ? `Pro · ${p.prices[region]} ${t("cad.life", "lifetime")}`
                          : `Prime · ${p.prices[region]}/${t("cad.month.short", "month")}`}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid gap-6 md:grid-cols-3">
                  <div>
                    <label className={labelCls} htmlFor="backer-name">{t("form.name", "Full name")}</label>
                    <input id="backer-name" data-testid="backer-name-input" required className={inputCls} value={form.name} onChange={set("name")} placeholder={t("form.ph.name", "Your name")} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="backer-email">{t("form.email", "Email")}</label>
                    <input id="backer-email" data-testid="backer-email-input" required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="backer-phone">{t("form.phone", "Contact number")}</label>
                    <input id="backer-phone" data-testid="backer-phone-input" required className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+1 ..." />
                  </div>
                </div>

                {status === "error" && (
                  <p data-testid="backer-error" className="text-base text-rose-300">
                    {payError || t("form.error", "Something went wrong. Please try again or email invest@thelifespanproject.com.")}
                  </p>
                )}

                <button
                  type="submit"
                  data-testid="backer-submit-button"
                  disabled={status === "sending"}
                  className="group mx-auto flex items-center gap-3 rounded-full bg-gradient-accent px-6 py-3.5 text-base font-semibold text-white shadow-[0_8px_32px_rgba(59,130,246,0.35)] transition-shadow duration-300 hover:shadow-[0_8px_44px_rgba(34,211,238,0.5)] disabled:opacity-60 sm:px-10 sm:py-4"
                >
                  {status === "sending"
                    ? t("ck.sending", "Opening secure checkout...")
                    : `${t("ck.submit", "Continue to Secure Payment")} · ${active.prices[region]}${active.mode === "monthly" ? t("ck.per.month", " / month") : t("ck.per.life", " lifetime")}`}
                  <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <p className="text-center text-base text-white/40">
                  {t("ck.note1", "Recorded as a")} {active.name} {t("ck.note2", "subscription. Receipt and tax invoice follow by email.")}
                </p>
                {region === "intl" && (
                  <p className="text-center text-sm font-light text-white/45" data-testid="checkout-inr-note">
                    {t("ck.inrnote", "International cards welcome. Razorpay bills in INR at the rupee price shown.")}
                  </p>
                )}
                <p className="text-center text-base font-light text-fog">
                  {t("ck.orwell", "Not ready to back the research?")}{" "}
                  <button
                    type="button"
                    onClick={() => setWwOpen(true)}
                    data-testid="checkout-cta-wellwisher"
                    className="link-underline font-medium text-white transition-colors duration-300 hover:text-cyan-200"
                  >
                    {t("cta.welllink", "Subscribe Free as a Wellwisher")}
                  </button>
                </p>

              </form>

              <div className="mt-8 border-t border-white/10 pt-6 text-center" data-testid="invite-block">
                  <button
                    type="button"
                    onClick={() => setInviteOpen((o) => !o)}
                    data-testid="invite-toggle"
                    className="text-sm font-semibold text-cyan-300/80 transition-colors hover:text-cyan-200"
                  >
                    {t("ck.invite.toggle", "Have an invite code?")}
                  </button>
                  {inviteOpen && (
                    <form onSubmit={redeemInvite} className="mx-auto mt-5 grid max-w-md gap-4 text-left">
                      <div>
                        <label className={labelCls} htmlFor="invite-code">{t("ck.invite.code", "Invite code")}</label>
                        <input id="invite-code" data-testid="invite-code-input" required className={inputCls} value={invite.code} onChange={setInv("code")} placeholder={t("ck.invite.ph", "Enter your code")} />
                      </div>
                      <div>
                        <label className={labelCls} htmlFor="invite-name">{t("form.name", "Full name")}</label>
                        <input id="invite-name" data-testid="invite-name-input" required className={inputCls} value={invite.name} onChange={setInv("name")} placeholder={t("form.ph.name", "Your name")} />
                      </div>
                      <div>
                        <label className={labelCls} htmlFor="invite-email">{t("form.email", "Email")}</label>
                        <input id="invite-email" data-testid="invite-email-input" required type="email" className={inputCls} value={invite.email} onChange={setInv("email")} placeholder="you@example.com" />
                      </div>
                      {inviteError && <p data-testid="invite-error" className="text-sm text-rose-300">{inviteError}</p>}
                      <button
                        type="submit"
                        data-testid="invite-redeem-button"
                        disabled={inviteStatus === "sending"}
                        className="bg-gradient-green flex min-h-[48px] items-center justify-center gap-2 rounded-full px-6 text-sm font-bold text-black disabled:opacity-60"
                      >
                        {inviteStatus === "sending" ? t("ck.invite.sending", "Applying your invite...") : t("ck.invite.submit", "Redeem free membership")}
                      </button>
                      <p className="text-center text-xs text-white/40">{t("ck.invite.note", "A valid invite makes the selected plan completely free.")}</p>
                    </form>
                  )}
                </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 14. CLOSING */}
      <section data-testid="closing-section" className="relative z-10 border-t border-white/5 bg-surface/50 py-24 md:py-32">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <motion.div {...fade()}>
            <h2 className="mx-auto max-w-4xl font-display text-[2rem] font-bold leading-[1.15] tracking-tight sm:text-[2.5rem] lg:text-[3rem]">
              {t("close.h2a", "Don't just watch the")} <span className="text-gradient">{t("close.h2b", "future of longevity.")}</span> {t("close.h2c", "Help build it.")}
            </h2>
            <p className="mx-auto mt-8 max-w-2xl text-lg font-light leading-relaxed text-fog">
              {t("close.p", "The Lifespan Project is building the research. The Lifespan Labs will bring the ecosystem together. Founding Members help make that journey possible, and become part of what comes next.")}
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <a
                href="#membership-checkout"
                onClick={() => choose("prime")}
                data-testid="closing-cta-prime"
                className="flex w-full items-center justify-center gap-3 rounded-full bg-gradient-green px-6 py-3.5 text-base font-semibold text-black shadow-[0_8px_32px_rgba(74,222,128,0.30)] transition-shadow duration-300 hover:shadow-[0_8px_44px_rgba(163,230,53,0.45)] sm:w-auto sm:px-8 sm:py-4"
              >
                {t("close.prime", "Become a Prime Member")}
                <ArrowUpRight className="h-4 w-4" />
              </a>
              <a
                href="#membership-checkout"
                onClick={() => choose("pro")}
                data-testid="closing-cta-pro"
                className="flex w-full items-center justify-center gap-3 rounded-full bg-gradient-accent px-6 py-3.5 text-base font-semibold text-white shadow-[0_8px_32px_rgba(59,130,246,0.35)] transition-shadow duration-300 hover:shadow-[0_8px_44px_rgba(34,211,238,0.5)] sm:w-auto sm:px-8 sm:py-4"
              >
                {t("close.pro", "Become a Pro Member")}
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
            <p className="mt-6 text-base font-light text-fog">
              {t("close.or", "or")}{" "}
              <button
                type="button"
                onClick={() => setWwOpen(true)}
                data-testid="closing-cta-wellwisher"
                className="link-underline font-medium text-white transition-colors duration-300 hover:text-cyan-200"
              >
                {t("cta.welllink", "Subscribe Free as a Wellwisher")}
              </button>
            </p>
          </motion.div>
        </div>
      </section>

      {/* RESEARCH SUBMISSION */}
      <section data-testid="research-submission" className="relative z-10 border-t border-white/5 py-24 md:py-32">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <motion.div {...fade()}>
            <FileText className="mx-auto h-8 w-8 text-cyan-300" />
            <h2 className="mt-6 font-display text-4xl font-semibold leading-[1.08] tracking-tight sm:text-5xl">
              {t("res.h2a", "Have an idea?")} <span className="text-gradient">{t("res.h2b", "Bring it to the Project.")}</span>
            </h2>
            <p className="mx-auto mt-8 max-w-2xl text-lg font-light leading-relaxed text-fog">
              The Lifespan Project is designed to bring together ideas and research
              from multiple disciplines. Founding Members may submit research papers,
              scientific ideas and proposals for consideration by our research and
              peer-review process. Selected work may be considered for publication,
              collaboration or further development within the Project.
            </p>
            <Link
              to="/apply"
              data-testid="submit-research-link"
              className="group mt-10 inline-flex items-center gap-3 rounded-full bg-gradient-accent px-8 py-4 text-base font-semibold text-white shadow-[0_8px_32px_rgba(59,130,246,0.35)] transition-shadow duration-300 hover:shadow-[0_8px_44px_rgba(34,211,238,0.5)]"
            >
              {t("res.cta", "Submit your research")}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <p className="mt-6 text-base font-light text-white/40">
              Submission does not guarantee publication, collaboration or acceptance.
            </p>
          </motion.div>
        </div>
      </section>

      {/* LEVEL 0 ASSESSMENT (secondary card) */}
      <section data-testid="assessment-secondary-card" className="relative z-10 border-t border-white/5 py-16">
        <div className="mx-auto max-w-3xl px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-3xl border border-white/10 bg-surface p-8 text-center"
          >
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-300">Free Level 0 Assessment · Beta</p>
            <h3 className="mt-3 font-display text-xl font-semibold sm:text-2xl">
              Let's check your current Lifespan Markers
            </h3>
            <p className="mx-auto mt-2 max-w-md text-sm font-light text-fog">
              Body, mind and longevity signals, read in 4-5 minutes. Free.
            </p>
            <Link
              to="/assessment?src=backus"
              data-testid="backus-assessment-cta"
              className="mt-5 inline-flex min-h-[48px] items-center gap-2 rounded-full border border-cyan-300/40 px-6 text-sm font-semibold text-cyan-200 transition-colors duration-300 hover:border-cyan-300/70 hover:text-white"
            >
              Take the Free Assessment
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>


      {/* DISCLAIMER */}
      <section data-testid="membership-disclaimer" className="relative z-10 border-t border-white/5 py-14">
        <div className="mx-auto max-w-4xl px-6">
          <p className="text-center text-base font-light leading-relaxed text-white/40">
            Important: Membership supports the activities of The Lifespan Project and
            provides access to the benefits described above. Membership is not an
            investment, donation, guarantee of scientific results, or promise of
            increased lifespan. Products and services available through The Lifespan
            Labs will vary and may be subject to separate terms, availability and
            eligibility requirements.
          </p>
        </div>
      </section>
      {/* WELLWISHER MODAL */}
      {wwOpen && (
        <div
          data-testid="wellwisher-modal"
          className="fixed inset-0 z-[110] flex items-center justify-center overflow-y-auto bg-black/70 px-6 py-10 backdrop-blur-md"
          onClick={closeWellwisher}
        >
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-xl rounded-3xl bg-gradient-to-br from-violet-500/40 via-blue-500/15 to-cyan-400/40 p-[1px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rounded-3xl bg-surface p-8 md:p-10">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-gradient font-mono text-sm uppercase tracking-[0.25em]">{t("ww.eyebrow", "Free forever")}</p>
                  <h3 className="mt-3 font-display text-2xl font-semibold tracking-tight">
                    {t("ww.title", "Become a Wellwisher Founding Member")}
                  </h3>
                  <p className="mt-3 text-base font-light leading-relaxed text-fog">
                    {t("ww.sub", "Research news and updates, and your Digital Wellwisher Membership card with scannable barcode, delivered instantly.")}
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Close"
                  data-testid="wellwisher-close"
                  onClick={closeWellwisher}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/15 text-white transition-colors duration-300 hover:border-cyan-300/50"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {wwStatus === "done" ? (
                <div data-testid="wellwisher-success" className="mt-8 text-center">
                  <p className="text-lg font-light text-fog">
                    {t("ww.success.p", "Welcome aboard. Your card is below, and a copy is on its way to your inbox.")}
                  </p>
                  {wwMemberId && (
                    <div className="mt-6">
                      <img
                        src={`${API}/membership/card/${wwMemberId}`}
                        alt="Your Wellwisher Founding Membership card"
                        data-testid="wellwisher-membership-card"
                        className="mx-auto w-full rounded-2xl border border-white/10 shadow-[0_16px_60px_rgba(59,130,246,0.18)]"
                      />
                      <p data-testid="wellwisher-membership-id" className="text-gradient mt-4 font-mono text-base font-semibold tracking-[0.2em]">
                        {wwMemberId}
                      </p>
                      <a
                        href={`${API}/membership/card/${wwMemberId}/download`}
                        data-testid="wellwisher-card-download"
                        className="bg-gradient-accent mt-6 inline-flex items-center gap-3 rounded-full px-8 py-3.5 text-base font-semibold text-white shadow-[0_8px_32px_rgba(59,130,246,0.35)] transition-shadow duration-300 hover:shadow-[0_8px_44px_rgba(34,211,238,0.5)]"
                      >
                        <Download className="h-4 w-4" />
                        {t("sub.download", "Download Membership Card")}
                      </a>
                    </div>
                  )}
                </div>
              ) : (
                <form data-testid="wellwisher-form" onSubmit={submitWellwisher} className="mt-8 grid gap-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className={labelCls} htmlFor="ww-name">{t("form.name", "Full name")}</label>
                      <input id="ww-name" data-testid="wellwisher-name-input" required className={inputCls} value={wwForm.name} onChange={setWw("name")} placeholder={t("form.ph.name", "Your name")} />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="ww-email">{t("form.email", "Email")}</label>
                      <input id="ww-email" data-testid="wellwisher-email-input" required type="email" className={inputCls} value={wwForm.email} onChange={setWw("email")} placeholder="you@example.com" />
                    </div>
                  </div>
                  <div className="grid gap-5 sm:grid-cols-3">
                    <div>
                      <label className={labelCls} htmlFor="ww-phone">{t("form.phone", "Contact number")}</label>
                      <input id="ww-phone" data-testid="wellwisher-phone-input" required className={inputCls} value={wwForm.phone} onChange={setWw("phone")} placeholder="+91 ..." />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="ww-country">{t("form.country", "Country")}</label>
                      <input id="ww-country" data-testid="wellwisher-country-input" required className={inputCls} value={wwForm.country} onChange={setWw("country")} placeholder={t("form.ph.country", "India")} />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="ww-city">{t("form.city", "City")}</label>
                      <input id="ww-city" data-testid="wellwisher-city-input" required className={inputCls} value={wwForm.city} onChange={setWw("city")} placeholder={t("form.ph.city", "Mumbai")} />
                    </div>
                  </div>
                  {wwStatus === "error" && (
                    <p data-testid="wellwisher-error" className="text-base text-rose-300">
                      {t("form.error", "Something went wrong. Please try again or email invest@thelifespanproject.com.")}
                    </p>
                  )}
                  <button
                    type="submit"
                    data-testid="wellwisher-submit-button"
                    disabled={wwStatus === "sending"}
                    className="group mx-auto flex items-center gap-3 rounded-full bg-gradient-accent px-6 py-3.5 text-base font-semibold text-white shadow-[0_8px_32px_rgba(59,130,246,0.35)] transition-shadow duration-300 hover:shadow-[0_8px_44px_rgba(34,211,238,0.5)] disabled:opacity-60 sm:px-10"
                  >
                    {wwStatus === "sending" ? t("ww.submitting", "Subscribing...") : t("cta.free", "Subscribe Free")}
                    <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </main>
  );
}
